// reportInvestigationList.js
import jsPDF from "jspdf";
import "jspdf-autotable";
import dayjs from "dayjs";
import "dayjs/locale/th";
import "../../../../assets/font/THSarabunNew-normal";
import "../../../../assets/font/THSarabunNew-bold";

dayjs.locale("th");

// ---------- helpers ----------
const toThaiDate = (d) => {
  const x = dayjs(d);
  if (!x.isValid()) return "-";
  const dd = x.format("D");
  const mmm = x.locale("th").format("MMM");
  const yyyyBE = x.year() + 543;
  return `${dd} ${mmm} ${yyyyBE}`;
};

const fmtMoney = (n) =>
  Number(n ?? 0).toLocaleString("th-TH", { maximumFractionDigits: 0 });

const mapInvestResult = (v) => {
  const s = Number(v);
  if (s === 0) return "ไม่เจอทรัพย์";
  if (s === 1) return "เจอทรัพย์";
  if (s === 2) return "อายัดเงินได้";
  return "-";
};

const propertyTypeLabel = (id) =>
  id === 1 ? "น.ส.4จ." : id === 2 ? "น.ส.3ก." : "-";

const propertyTypeDetailLabel = (id) =>
  id === 1
    ? "ที่ดินว่างเปล่า"
    : id === 2
    ? "ที่ตาบอด"
    : id === 3
    ? "ที่ดินพร้อมสิ่งปลูกสร้าง"
    : id === 4
    ? "ที่นา"
    : id === 5
    ? "ห้องชุด"
    : id === 6
    ? "บ้านเอื้ออาทร"
    : id === 7
    ? "ที่ดินต้องห้าม"
    : id === 8
    ? "จดรวม 2 โฉนด"
    : id === 9
    ? "ที่ถนนทางเข้า"
    : id === 10
    ? "ไม่ได้ลงพื้นที่ตรวจสอบ"
    : "-";

const fmtArea = (rai, ngan, wa, subwa) => {
  const parts = [];
  if (rai) parts.push(`${rai} ไร่`);
  if (ngan) parts.push(`${ngan} งาน`);
  if (wa) parts.push(`${wa} ตร.วา`);
  if (subwa) parts.push(`${subwa} ส่วนวา`);
  return parts.join(" ") || "-";
};

// ---------- table head ----------
const tableHead = [
  [
    "ลำดับ",
    "เลขสัญญา",
    "สถานะทรัพย์",
    "วันที่สืบ",
    "ประเภททรัพย์",
    "รายละเอียดทรัพย์",
    "เจ้าของทรัพย์",
    "ติดภาระ",
    "บังคับคดี",
    "หมายเหตุ",
    "ผู้รับผิดชอบ",
  ],
];

/**
 * แปลง rawList -> rows สำหรับ autoTable
 */
function buildBodyRows(rawList = []) {
  const rows = [];
  let rowNo = 1;

  (rawList || []).forEach((r) => {
    console.log("r", r);

    const contno =
      [
        r?.CONTNO ? `${r.CONTNO}` : null,
        r?.CUSTOMER_TNAME ? `${r.CUSTOMER_TNAME}${r.CUSTOMER_FNAME} ` : null,
        r?.CUSTOMER_LNAME ? `${r.CUSTOMER_LNAME}` : null,
      ]
        .filter(Boolean)
        .join("\n") || "-";
    const assigeName = r.LAWYER_NNAME;
    const statusTxt = `${mapInvestResult(r?.investigation_status)} สืบรอบที่${
      r?.round_no
    }`;

    const invDate =
      [
        r?.investigation_date ? toThaiDate(r?.investigation_date) : "-",
        r?.department_of_lands ? r?.department_of_lands : "-",
      ]
        .filter(Boolean)
        .join("\n") || "-";

    const props = Array.isArray(r?.property_list) ? r.property_list : [];
    const lastLog = r?.mark;

    if (props.length === 0) {
      rows.push([
        String(rowNo++),
        contno,
        statusTxt,
        invDate,
        "-",
        "-",
        "-",
        "-",
        "-",
        lastLog || "-",
        assigeName,
      ]);
      return;
    }

    props.forEach((p) => {
      console.log("p", p);

      const type = [
        p?.property_type_id ? propertyTypeLabel(p?.property_type_id) : "-",
        p?.property_detail_id
          ? `ลักษณะที่ ${propertyTypeDetailLabel(p?.property_detail_id)}`
          : "-",
      ];
      const detail =
        [
          p?.deed_number ? `${p.deed_number}` : null,
          fmtArea(p?.rai, p?.ngan, p?.wa, p?.subwa),
          p?.district_desc ? `อ.${p.district_desc}` : null,
          p?.province_desc ? `จ.${p.province_desc}` : null,
        ]
          .filter(Boolean)
          .join("\n") || "-";

      const ownerPoss =
        [p?.possessor ? `${p.possessor}` : null].filter(Boolean).join("\n") ||
        "-";

      const burden =
        [
          p?.mortgage_type ? `(${p.mortgage_type})` : null,
          p?.mortgagee ? `ผู้รับ: ${p.mortgagee}` : null,
          (p?.mortgage_balance ?? null) != null
            ? `${fmtMoney(p.mortgage_balance)} บาท`
            : null,
        ]
          .filter(Boolean)
          .join("\n") || "-";

      const enforce =
        [
          p?.sequestrate_status === 1 ? "มีคำสั่งยึด/อายัด" : null,
          (p?.estimated_enforce_price ?? null) != null
            ? `ประเมิน: ${fmtMoney(p.estimated_enforce_price)} บาท`
            : null,
          p?.legal_execution_office
            ? `สนง.บังคับคดี: ${p.legal_execution_office}`
            : null,
          p?.seize_date ? `ยึดเมื่อ: ${toThaiDate(p.seize_date)}` : null,
        ]
          .filter(Boolean)
          .join("\n") || "-";

      const note =
        [lastLog?.mark || null, p?.mark || null].filter(Boolean).join("\n") ||
        "-";
      const invDate =
        [
          r?.investigation_date ? toThaiDate(r?.investigation_date) : "-",
          r?.department_of_lands ? r?.department_of_lands : "-",
        ]
          .filter(Boolean)
          .join("\n") || "-";

      rows.push([
        String(rowNo++),
        contno,
        statusTxt,
        invDate,
        type,
        detail,
        ownerPoss,
        burden,
        enforce,
        note,
        assigeName,
      ]);
    });
  });

  return rows;
}

// ---------- main ----------
export function printInvestigationReportList(rawList = []) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = { l: 12, r: 12, t: 14, b: 12 };

  doc.setFont("THSarabunNew", "bold");

  const body = buildBodyRows(rawList);

  doc.autoTable({
    head: tableHead,
    body,
    startY: margin.t + 16, // หน้าแรกเว้นที่สำหรับหัวกระดาษ
    margin: { left: margin.l, right: margin.r, bottom: margin.b },
    styles: {
      font: "THSarabunNew",
      fontSize: 11, // ↓ ลดฟอนต์เพื่อให้ wrap ดีขึ้น
      overflow: "linebreak",
      cellPadding: 2,
      valign: "top",
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [30, 77, 140],
      textColor: 255,
      fontStyle: "normal",
      fontSize: 11,
      halign: "center",
    },
    // ✅ จัดสัดส่วนคอลัมน์ใหม่ (รวม ~268 มม. ภายในพื้นที่พิมพ์ 273 มม.)
    columnStyles: {
      0: { halign: "center", cellWidth: 10 }, // ลำดับ
      1: { halign: "center", cellWidth: 20 }, // เลขสัญญา
      2: { halign: "center", cellWidth: 18 }, // สถานะทรัพย์
      3: { halign: "center", cellWidth: 25 }, // วันที่สืบ
      4: { halign: "center", cellWidth: 25 }, // ประเภททรัพย์
      5: { halign: "center", cellWidth: 30 }, // รายละเอียดทรัพย์
      6: { halign: "center", cellWidth: 26 }, // เจ้าของทรัพย์
      7: { halign: "center", cellWidth: 30 }, // ติดภาระ
      8: { halign: "center", cellWidth: 30 }, // บังคับคดี/ราคาประเมิน
      9: { halign: "center", cellWidth: 35 }, // ที่ตั้ง
      10: { halign: "center", cellWidth: 25 }, // หมายเหตุ
    },
    tableWidth: pageW - margin.l - margin.r,
    didDrawPage: (data) => {
      // ✅ หัวกระดาษเฉพาะหน้าแรก
      if (data.pageNumber === 1) {
        doc.setFont("THSarabunNew", "bold");
        doc.setFontSize(18);
        doc.text("รายงานผลการสืบทรัพย์", pageW / 2, margin.t, {
          align: "center",
        });
        doc.setFontSize(12);
        doc.text(
          `วันที่พิมพ์: ${toThaiDate(new Date())}`,
          pageW / 2,
          margin.t + 8,
          {
            align: "center",
          }
        );
      }

      // ✅ เลขหน้าทุกหน้า
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(10);
      doc.text(
        `หน้า ${data.pageNumber}/${pageCount}`,
        pageW - margin.r,
        pageH - 6,
        {
          align: "right",
        }
      );
    },
  });

  // สรุปท้าย
  const endY = doc.lastAutoTable?.finalY || pageH - margin.b - 10;
  doc.setFontSize(12);
  doc.text(
    `รวมทั้งหมด: ${body.length.toLocaleString("th-TH")} `,
    margin.l,
    endY + 8
  );

  const fileName = `investigation_summary_${dayjs().format("YYYYMMDD")}.pdf`;
  try {
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    const w = window.open(url);
    if (w) w.onload = () => w.print();
    else doc.save(fileName);
  } catch {
    doc.save(fileName);
  }
}
